import { defineStore } from 'pinia'
import { getChangelogEntries, getChangelogUnread, markChangelogViewed } from '@/api/changelog'

/**
 * Drives the Changelog slideout (one panel instance mounted in AppLayout) and
 * the unread dots on its triggers (navbar bullhorn, user menu, MLAP sub-nav).
 * `feed` picks which feed the panel shows: 'general' (sitewide) or 'mlap'.
 *
 * Feed switches race-protect via `activeRequestId`: every open() supersedes
 * in-flight loads, whose responses are then ignored — otherwise a quick
 * general → mlap switch could render one feed's entries under the other's
 * title (both triggers coexist on MLAP pages).
 */
export const useChangelogStore = defineStore('changelog', {
  state: () => ({
    isOpen: false,
    feed: 'general',
    entries: [],
    page: 0,
    hasMore: false,
    loading: false,
    loadFailed: false,
    unread: { general: false, mlap: false },
    activeRequestId: 0
  }),

  actions: {
    async open(feed) {
      this.activeRequestId += 1
      this.feed = feed
      this.isOpen = true
      this.entries = []
      this.page = 0
      this.hasMore = false
      this.loading = false // a superseded in-flight load no longer owns this flag
      this.loadFailed = false
      this.markViewed(feed)
      await this.loadMore()
    },

    close() {
      this.isOpen = false
    },

    async loadMore() {
      if (this.loading) return
      const requestId = this.activeRequestId
      this.loading = true
      this.loadFailed = false
      try {
        const { data } = await getChangelogEntries(this.feed, this.page + 1)
        if (requestId !== this.activeRequestId) return // superseded by a newer open()
        this.entries.push(...data.entries)
        this.page += 1
        this.hasMore = data.meta.hasMore
      } catch {
        if (requestId === this.activeRequestId) this.loadFailed = true
      } finally {
        if (requestId === this.activeRequestId) this.loading = false
      }
    },

    // Both dot updates are best-effort — a failed request must never break the page.
    async fetchUnread(feed) {
      try {
        const { data } = await getChangelogUnread(feed)
        this.unread[feed] = data.unread
      } catch { /* keep the dot hidden */ }
    },

    async markViewed(feed) {
      this.unread[feed] = false
      try {
        await markChangelogViewed(feed)
      } catch { /* re-shows on next page load at worst */ }
    }
  }
})
